/**
 * LogoUpload Component
 *
 * Drag-and-drop zone with click-to-upload for token logo images.
 * Client-side validates file type (PNG, JPEG, WebP, SVG) and size (< 2MB).
 * Shows preview on success with remove/replace option.
 */

import { type ChangeEvent, type DragEvent, useCallback, useRef, useState } from "react";

import { cn } from "../../lib/cn";
import { Spinner } from "../ui/Spinner";

const ALLOWED_TYPES = ["image/png", "image/jpeg", "image/webp", "image/svg+xml"];
const ALLOWED_ACCEPT = ALLOWED_TYPES.join(",");
const MAX_SIZE = 2_097_152; // 2MB

type LogoUploadProps = {
	onUploadComplete: (uploadId: string, url: string) => void;
	onRemove: () => void;
	uploadFn: (file: File) => Promise<{ data: { id: string; url: string } }>;
	previewUrl?: string | null;
	onPreviewChange?: (blobUrl: string | null) => void;
	disabled?: boolean;
};

export function LogoUpload({
	onUploadComplete,
	onRemove,
	uploadFn,
	previewUrl = null,
	onPreviewChange,
	disabled = false,
}: LogoUploadProps) {
	const [dragOver, setDragOver] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [uploading, setUploading] = useState(false);
	const [localPreview, setLocalPreview] = useState<string | null>(null);
	const inputRef = useRef<HTMLInputElement>(null);

	// Prefer local blob URL (current upload) over previewUrl (API path used for draft restoration)
	const preview = localPreview ?? previewUrl;

	const validateFile = useCallback((file: File): string | null => {
		if (!ALLOWED_TYPES.includes(file.type)) {
			return `Invalid file type: ${file.type}. Accepted: PNG, JPEG, WebP, SVG.`;
		}
		if (file.size > MAX_SIZE) {
			return `File too large: ${(file.size / 1_048_576).toFixed(1)}MB. Maximum: 2MB.`;
		}
		if (file.size === 0) {
			return "File is empty.";
		}
		return null;
	}, []);

	const handleFile = useCallback(
		async (file: File) => {
			setError(null);

			const validationError = validateFile(file);
			if (validationError) {
				setError(validationError);
				return;
			}

			// Show local preview immediately
			const objectUrl = URL.createObjectURL(file);
			setLocalPreview(objectUrl);
			onPreviewChange?.(objectUrl);
			setUploading(true);

			try {
				const result = await uploadFn(file);
				onUploadComplete(result.data.id, result.data.url);
			} catch (err) {
				setLocalPreview(null);
				onPreviewChange?.(null);
				URL.revokeObjectURL(objectUrl);
				setError(err instanceof Error ? err.message : "Upload failed. Please try again.");
			} finally {
				setUploading(false);
			}
		},
		[validateFile, uploadFn, onUploadComplete, onPreviewChange]
	);

	const handleDrop = useCallback(
		(e: DragEvent<HTMLButtonElement>) => {
			e.preventDefault();
			setDragOver(false);
			if (disabled || uploading) return;

			const file = e.dataTransfer.files[0];
			if (file) {
				void handleFile(file);
			}
		},
		[disabled, uploading, handleFile]
	);

	const handleDragOver = useCallback(
		(e: DragEvent<HTMLButtonElement>) => {
			e.preventDefault();
			if (!disabled && !uploading) {
				setDragOver(true);
			}
		},
		[disabled, uploading]
	);

	const handleDragLeave = useCallback(() => {
		setDragOver(false);
	}, []);

	const handleInputChange = useCallback(
		(e: ChangeEvent<HTMLInputElement>) => {
			const file = e.target.files?.[0];
			if (file) {
				void handleFile(file);
			}
			// Reset input so re-selecting the same file triggers onChange
			if (inputRef.current) {
				inputRef.current.value = "";
			}
		},
		[handleFile]
	);

	const handleClick = useCallback(() => {
		if (!disabled && !uploading) {
			inputRef.current?.click();
		}
	}, [disabled, uploading]);

	const handleRemove = useCallback(() => {
		if (localPreview) {
			URL.revokeObjectURL(localPreview);
		}
		setLocalPreview(null);
		onPreviewChange?.(null);
		setError(null);
		onRemove();
	}, [localPreview, onRemove, onPreviewChange]);

	if (preview) {
		return (
			<div className="relative inline-block">
				<img
					src={preview}
					alt="Token logo preview"
					className="h-24 w-24 rounded-xl object-cover border border-pastel-border"
				/>
				{uploading && (
					<div className="absolute inset-0 flex items-center justify-center rounded-xl bg-white/70">
						<Spinner size="sm" />
					</div>
				)}
				{!uploading && !disabled && (
					<button
						type="button"
						onClick={handleRemove}
						className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full bg-pastel-black text-white text-xs hover:bg-red-500 transition-colors"
						aria-label="Remove logo"
					>
						x
					</button>
				)}
			</div>
		);
	}

	return (
		<div>
			<button
				type="button"
				onClick={handleClick}
				onDrop={handleDrop}
				onDragOver={handleDragOver}
				onDragLeave={handleDragLeave}
				disabled={disabled}
				className={cn(
					"flex h-24 w-24 cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed transition-colors",
					dragOver ? "border-pastel-vibrant-blue bg-pastel-vibrant-blue/5" : "border-pastel-border",
					disabled && "opacity-50 cursor-not-allowed",
					!disabled && "hover:border-pastel-vibrant-blue hover:bg-pastel-light-gray"
				)}
			>
				{uploading ? (
					<Spinner size="sm" />
				) : (
					<>
						<span className="text-2xl text-pastel-gray">+</span>
						<span className="text-xs text-pastel-gray mt-1">Logo</span>
					</>
				)}
			</button>
			{error && <p className="mt-1 text-xs text-red-500">{error}</p>}
			<input
				ref={inputRef}
				type="file"
				accept={ALLOWED_ACCEPT}
				onChange={handleInputChange}
				className="hidden"
				aria-label="Upload logo file"
			/>
		</div>
	);
}
